import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';

import Header, { LanguageSwitcher } from './Header';
import InfoSidebar from './InfoSidebar';
import ProductStep from './steps/ProductStep';
import CapsuleSizeStep from './steps/CapsuleSizeStep';
import MaterialStep from './steps/MaterialStep';
import LeasingStep from './steps/LeasingStep';
import IndicationsTable from './IndicationsTable';
import ResultsStep from './steps/ResultsStep';
import LaborResultsStep from './steps/LaborResultsStep';
import OptionalLayersStep from './steps/OptionalLayersStep';
import LeadFormGate from './LeadFormGate';
import PdfDownloadButton from './PdfDownloadButton';
import { loadLanguage, localizeSettings } from './i18n';
import {
	createUuid,
	storedPdfUrl,
	storePdf,
	hasSubmittedLead,
	rememberSubmittedLead,
} from './pdf';
import {
	calculateResults,
	calculateLaborResults,
	calculateMonthlyRate,
	calculateChairTimeLayer,
	calculateFinancingLayer,
	calculateCameraLayer,
} from './roiMath';
import './style.scss';

const DEFAULT_LEASE_TERM_MONTHS = 60;

// "Optional layers" (Praxis only) starting values — generic financial
// assumptions the reference tool (simuprint.vercel.app) hardcodes rather
// than deriving from product data, same as this constant above.
const DEFAULT_REPLACEMENT_ACT_FEE = 150;
const DEFAULT_TARGET_CONVERSION_RATE = 80;
const DEFAULT_PROVISIONAL_CASES_AVOIDED = 8;
const DEFAULT_SAVING_PER_PROVISIONAL = 25;

const byId = ( items, key ) =>
	Object.fromEntries( items.map( ( item ) => [ item.id, item[ key ] ] ) );

// The design-cost "source" an indication starts on: AI (free) for Midas
// indications, SR (SprintRay design service) for everything else — mirrors
// which pill is highlighted by default on the reference tool.
const defaultDesignSource = ( indication ) =>
	indication.hasAiDesignOption ? 'ai' : 'sr';

// Indications belong to the selected product (Midas and Pro 2 show entirely
// different indication tables) — build the page's default state for a
// freshly (re-)selected product in one place, since both the initial
// product and every later product switch need the exact same reset.
const buildIndicationDefaults = ( product ) => {
	const indications = product?.indications ?? [];

	return {
		enabledIndications: byId( indications, 'enabledByDefault' ),
		casesPerMonth: byId( indications, 'defaultCasesPerMonth' ),
		labOutsourcingCost: byId( indications, 'defaultLabOutsourcingCost' ),
		materialCost: byId( indications, 'defaultMaterialCost' ),
		designCost: byId( indications, 'defaultDesignCost' ),
		designSource: Object.fromEntries(
			indications.map( ( indication ) => [
				indication.id,
				defaultDesignSource( indication ),
			] )
		),
	};
};

// The distinct MIDAS capsule sizes ('single-unit' / 'multi-unit') actually
// present among a product's materials, in first-seen order — blank/unset
// materials (e.g. Pro 2, or every Midas material before it's tagged) are
// filtered out, since they're "not capsule-specific", not a size choice of
// their own. A product with 0 or 1 distinct size here never shows the
// capsule-size step at all (see availableCapsuleSizes in the component
// below) — that's what keeps today's single-capsule catalog unaffected
// until materials are actually tagged with more than one size in ACF.
//
// `showMultiunitCapsule === false` (Settings.php's `show_multiunit_capsule`
// field, per-product) drops 'multi-unit' here even if Multiunit materials
// exist in the catalog — the admin-facing "go back to Single Unit only"
// override, independent of whatever's actually tagged in ACF.
const distinctCapsuleSizes = ( product ) => {
	const sizes = Array.from(
		new Set(
			( product?.materials ?? [] )
				.map( ( material ) => material.capsuleSize )
				.filter( Boolean )
		)
	);

	return product?.showMultiunitCapsule === false
		? sizes.filter( ( size ) => size !== 'multi-unit' )
		: sizes;
};

// A single, always-visible page — mirrors the reference tool
// (simuprint.vercel.app), which shows the device picker, indications
// table, and live results all at once with no "Next" step. Not a wizard:
// every section below reacts to every input immediately.
const Calculator = ( { settings, languageSwitcher } ) => {
	const products = settings?.products ?? [];
	const currency = settings?.currency ?? 'EUR';
	const locale = settings?.locale;
	const calculatorType = settings?.calculatorType ?? 'praxis';
	const isLabor = calculatorType === 'labor';
	// Praxis-only "Optional layers" section, toggleable per calculator
	// instance (PostTypes.php's `show_optional_layers` field) — defaults to
	// shown when the setting is missing from the payload entirely (e.g. a
	// stale cached settings blob from before this existed), matching
	// Settings.php's own "unset means shown" default.
	const showOptionalLayers = settings?.showOptionalLayers ?? true;
	// Per-card visibility and text overrides on top of that (Settings.php's
	// build_optional_layers()) — missing from a stale payload means every
	// card shown with its default texts, same as before these existed.
	const optionalLayers = {
		chairTime: { show: true, ...settings?.optionalLayers?.chairTime },
		financing: { show: true, ...settings?.optionalLayers?.financing },
		camera: { show: true, ...settings?.optionalLayers?.camera },
	};
	// HubSpot lead form gating the results (Settings.php's `leadForm`, null
	// when this calculator shows its results right away).
	const leadForm = settings?.leadForm ?? null;
	const pdfSettings = settings?.pdf ?? null;

	const [ leadSubmitted, setLeadSubmitted ] = useState(
		() => ! leadForm || hasSubmittedLead( settings?.calculatorId )
	);
	// Generated up front: its URL goes into the lead form's hidden field
	// before the PDF itself is stored there (see LeadFormGate.js).
	const [ storedPdfId ] = useState( createUuid );

	const [ selectedProductId, setSelectedProductId ] = useState(
		products[ 0 ]?.id ?? null
	);
	const [ pricePaid, setPricePaid ] = useState(
		products[ 0 ]?.listPrice ?? 0
	);
	const [ selectedMaterialId, setSelectedMaterialId ] = useState(
		products[ 0 ]?.materials?.[ 0 ]?.id ?? null
	);
	// MIDAS capsule size ('single-unit' / 'multi-unit') — only meaningful
	// once a product actually has materials tagged with more than one
	// distinct size (see availableCapsuleSizes below); null on every
	// product until then, which keeps today's single-capsule catalog
	// behaving exactly as before. initialCapsuleSizes() mirrors
	// selectedMaterialId's "first item of the initial product" init above.
	const [ selectedCapsuleSize, setSelectedCapsuleSize ] = useState(
		() => distinctCapsuleSizes( products[ 0 ] )[ 0 ] ?? null
	);
	const [ indicationState, setIndicationState ] = useState( () =>
		buildIndicationDefaults( products[ 0 ] )
	);
	const [ leaseTermMonths, setLeaseTermMonths ] = useState(
		products[ 0 ]?.leaseTermMonths ?? DEFAULT_LEASE_TERM_MONTHS
	);
	const [ annualInterestRate, setAnnualInterestRate ] = useState(
		products[ 0 ]?.annualInterestRate ?? 0
	);
	const [ contractualRateOverride, setContractualRateOverride ] =
		useState( null );

	// "Optional layers" (Praxis only) — all start disabled, mirroring the
	// reference tool, and hold their own local state independent of the
	// main calculation (see roiMath.js's calculateChairTimeLayer /
	// calculateFinancingLayer / calculateCameraLayer).
	const [ chairTimeLayerEnabled, setChairTimeLayerEnabled ] =
		useState( false );
	const [ replacementActFee, setReplacementActFee ] = useState(
		DEFAULT_REPLACEMENT_ACT_FEE
	);
	const [ targetConversionRate, setTargetConversionRate ] = useState(
		DEFAULT_TARGET_CONVERSION_RATE
	);
	const [ provisionalCasesAvoided, setProvisionalCasesAvoided ] = useState(
		DEFAULT_PROVISIONAL_CASES_AVOIDED
	);
	const [ savingPerProvisional, setSavingPerProvisional ] = useState(
		DEFAULT_SAVING_PER_PROVISIONAL
	);
	const [ financingLayerEnabled, setFinancingLayerEnabled ] =
		useState( false );
	const [ cameraLayerEnabled, setCameraLayerEnabled ] = useState( false );
	const [ cameraMonthlyPayment, setCameraMonthlyPayment ] = useState( 0 );

	const selectedProduct =
		products.find( ( product ) => product.id === selectedProductId ) ??
		null;
	const allIndications = selectedProduct?.indications ?? [];
	const availableCapsuleSizes = distinctCapsuleSizes( selectedProduct );
	// Materials/indications with no capsule size stay visible regardless of
	// the selection (e.g. Pro 2); a set size only shows once it matches.
	// When availableCapsuleSizes has fewer than 2 entries this is a no-op
	// (every material/indication either has no size, or the one size that
	// exists is always the "selected" one) — today's catalog behaves
	// exactly as before until a product actually gets a second size.
	const capsuleFilteredMaterials = (
		selectedProduct?.materials ?? []
	).filter(
		( material ) =>
			! material.capsuleSize ||
			material.capsuleSize === selectedCapsuleSize
	);
	const capsuleFilteredIndications = allIndications.filter(
		( indication ) =>
			! indication.capsuleSize ||
			indication.capsuleSize === selectedCapsuleSize
	);
	const activeMaterialName =
		capsuleFilteredMaterials.find(
			( material ) => material.id === selectedMaterialId
		)?.name ?? null;

	const activeIndications = capsuleFilteredIndications.filter(
		( indication ) => indicationState.enabledIndications[ indication.id ]
	);

	const calculatedMonthlyRate = calculateMonthlyRate(
		pricePaid,
		leaseTermMonths,
		annualInterestRate
	);

	// "Chair time freed" values from your N Midas cases/month" — the same
	// "first device group" a product's material step applies to (see
	// selectMaterial's materialGroup below), summed across active
	// indications only (confirmed live: disabling one Midas row recomputes
	// this down, not just the disabled row's own savings).
	const chairTimeDeviceGroup = capsuleFilteredIndications[ 0 ]?.deviceGroup;
	const freedSlotsPerMonth = activeIndications
		.filter(
			( indication ) => indication.deviceGroup === chairTimeDeviceGroup
		)
		.reduce(
			( sum, indication ) =>
				sum +
				( indicationState.casesPerMonth[ indication.id ] ??
					indication.defaultCasesPerMonth ??
					0 ),
			0
		);

	const results = useMemo( () => {
		if ( isLabor ) {
			return calculateLaborResults( {
				indications: activeIndications,
				casesPerMonthByIndication: indicationState.casesPerMonth,
				// Same field as Praxis' "lab outsourcing cost" — the reference
				// tool reuses it as "billing price" for Labor (see Settings.php).
				billingPriceByIndication: indicationState.labOutsourcingCost,
				materialCostByIndication: indicationState.materialCost,
				devicePricePaid: pricePaid,
				leaseTermMonths,
				annualInterestRate,
				contractualRateOverride,
			} );
		}

		return calculateResults( {
			indications: activeIndications,
			casesPerMonthByIndication: indicationState.casesPerMonth,
			labOutsourcingCostByIndication: indicationState.labOutsourcingCost,
			materialCostByIndication: indicationState.materialCost,
			designCostByIndication: indicationState.designCost,
			devicePricePaid: pricePaid,
		} );
	}, [
		isLabor,
		activeIndications,
		indicationState,
		pricePaid,
		leaseTermMonths,
		annualInterestRate,
		contractualRateOverride,
	] );

	// "Optional layers" (Praxis only) — cheap to recompute on every render,
	// same as calculatedMonthlyRate above, so these skip useMemo.
	const chairTimeLayer = calculateChairTimeLayer( {
		freedSlotsPerMonth,
		replacementActFee: replacementActFee ?? 0,
		targetConversionRatePercent: targetConversionRate ?? 0,
		provisionalCasesAvoidedPerMonth: provisionalCasesAvoided ?? 0,
		savingPerProvisional: savingPerProvisional ?? 0,
	} );
	const financingLayer = calculateFinancingLayer( {
		monthlySavings: results.monthlySavings ?? 0,
		calculatedMonthlyRate,
		contractualRateOverride,
	} );
	const cameraLayer = calculateCameraLayer( {
		devicePricePaid: pricePaid,
		monthlySavings: results.monthlySavings ?? 0,
		monthsForRoi: results.monthsForRoi ?? null,
		cameraMonthlyPayment: cameraMonthlyPayment ?? 0,
	} );

	// The chair-time card additionally needs a material step (Midas) — see
	// OptionalLayersStep.js. No visible card left = no empty section.
	const hasVisibleOptionalLayers =
		showOptionalLayers &&
		( ( selectedProduct?.hasMaterialStep &&
			optionalLayers.chairTime.show ) ||
			optionalLayers.financing.show ||
			optionalLayers.camera.show );

	// Inputs only — PdfReport.php recomputes every result server-side.
	// `results.perIndication` already holds each active row's resolved
	// values (user edit, else the indication's default).
	const buildPdfInput = () => ( {
		calculatorId: settings?.calculatorId,
		// The PDF comes out in the language shown on the page.
		locale: settings?.pdfLocale,
		productId: selectedProductId,
		materialId: selectedProduct?.hasMaterialStep
			? selectedMaterialId
			: null,
		pricePaid,
		leaseTermMonths,
		annualInterestRate,
		contractualRateOverride,
		indications: results.perIndication.map( ( row ) => ( {
			id: row.indicationId,
			casesPerMonth: row.casesPerMonth,
			price: isLabor ? row.billingPrice : row.labOutsourcingCost,
			materialCost: row.materialCost,
			designCost: isLabor ? 0 : row.designCost,
		} ) ),
	} );

	const handleLeadSubmitted = () => {
		storePdf( pdfSettings, storedPdfId, buildPdfInput() ).catch(
			( error ) =>
				// eslint-disable-next-line no-console
				console.error( 'ROI Calculator:', error )
		);
		rememberSubmittedLead( settings.calculatorId );
		setLeadSubmitted( true );
	};

	if ( ! settings || products.length === 0 ) {
		return (
			<p className="roi-calculator__empty">
				{ __(
					'This calculator has no products configured yet.',
					'sprintray-roi-calculator'
				) }
			</p>
		);
	}

	const selectProduct = ( product ) => {
		setSelectedProductId( product.id );
		setPricePaid( product.listPrice );
		// The material list itself may span more than one capsule size
		// (see distinctCapsuleSizes below) — default to the first material
		// that's actually visible under the newly-defaulted capsule size,
		// not just the first material overall.
		const initialCapsuleSize = distinctCapsuleSizes( product )[ 0 ] ?? null;
		setSelectedCapsuleSize( initialCapsuleSize );
		setSelectedMaterialId(
			( product.materials ?? [] ).find(
				( material ) =>
					! material.capsuleSize ||
					material.capsuleSize === initialCapsuleSize
			)?.id ?? null
		);
		// Different product = different indication catalog entirely, so the
		// whole per-indication state resets to that product's defaults.
		setIndicationState( buildIndicationDefaults( product ) );
		setLeaseTermMonths(
			product.leaseTermMonths ?? DEFAULT_LEASE_TERM_MONTHS
		);
		setAnnualInterestRate( product.annualInterestRate ?? 0 );
		setContractualRateOverride( null );
		// "Optional layers" reset to their defaults too, same as every other
		// per-product piece of state above.
		setChairTimeLayerEnabled( false );
		setReplacementActFee( DEFAULT_REPLACEMENT_ACT_FEE );
		setTargetConversionRate( DEFAULT_TARGET_CONVERSION_RATE );
		setProvisionalCasesAvoided( DEFAULT_PROVISIONAL_CASES_AVOIDED );
		setSavingPerProvisional( DEFAULT_SAVING_PER_PROVISIONAL );
		setFinancingLayerEnabled( false );
		setCameraLayerEnabled( false );
		setCameraMonthlyPayment( 0 );
	};

	const selectMaterial = ( material ) => {
		setSelectedMaterialId( material.id );
		// Mirrors the reference tool: switching material bulk-updates the
		// material cost for every indication *of that device* (each row
		// stays individually editable afterwards) — confirmed live against
		// the combined ecosystem product, where changing Midas's material
		// leaves all 11 Pro 2 rows' material costs untouched. The material
		// step only ever belongs to the first device group present, so
		// scope the bulk-update to indications sharing that same group
		// (single-group products like Midas-alone or Pro-2-alone have every
		// indication in that one group already, so this is a no-op there).
		// Scoped from capsuleFilteredIndications (not allIndications) so
		// picking a material for the *selected* capsule size never touches
		// the other capsule size's rows, which aren't even visible right now.
		const materialGroup = capsuleFilteredIndications[ 0 ]?.deviceGroup;
		const affectedIndications = capsuleFilteredIndications.filter(
			( indication ) => indication.deviceGroup === materialGroup
		);

		setIndicationState( ( prev ) => ( {
			...prev,
			materialCost: {
				...prev.materialCost,
				...byId(
					affectedIndications.map( ( indication ) => ( {
						id: indication.id,
						costPerCase: material.costPerCase,
					} ) ),
					'costPerCase'
				),
			},
		} ) );
	};

	// Switching capsule size re-derives which materials/indications are in
	// play (see capsuleFilteredMaterials/capsuleFilteredIndications above,
	// computed for the *current* selectedCapsuleSize) — so this recomputes
	// the same two things fresh for the *new* size explicitly, rather than
	// calling selectMaterial() (whose closed-over capsuleFilteredIndications
	// would still reflect the size being switched away from).
	const selectCapsuleSize = ( size ) => {
		setSelectedCapsuleSize( size );

		const materialsForSize = ( selectedProduct?.materials ?? [] ).filter(
			( material ) =>
				! material.capsuleSize || material.capsuleSize === size
		);
		const material = materialsForSize[ 0 ] ?? null;
		setSelectedMaterialId( material?.id ?? null );

		if ( ! material ) {
			return;
		}

		const indicationsForSize = allIndications.filter(
			( indication ) =>
				! indication.capsuleSize || indication.capsuleSize === size
		);
		const materialGroup = indicationsForSize[ 0 ]?.deviceGroup;
		const affectedIndications = indicationsForSize.filter(
			( indication ) => indication.deviceGroup === materialGroup
		);

		setIndicationState( ( prev ) => ( {
			...prev,
			materialCost: {
				...prev.materialCost,
				...byId(
					affectedIndications.map( ( indication ) => ( {
						id: indication.id,
						costPerCase: material.costPerCase,
					} ) ),
					'costPerCase'
				),
			},
		} ) );
	};

	const toggleIndication = ( id ) =>
		setIndicationState( ( prev ) => ( {
			...prev,
			enabledIndications: {
				...prev.enabledIndications,
				[ id ]: ! prev.enabledIndications[ id ],
			},
		} ) );

	const updateIndicationField = ( field ) => ( id, value ) =>
		setIndicationState( ( prev ) => ( {
			...prev,
			[ field ]: { ...prev[ field ], [ id ]: value },
		} ) );

	const resetIndication = ( id ) => {
		const indication = allIndications.find( ( row ) => row.id === id );

		if ( ! indication ) {
			return;
		}

		setIndicationState( ( prev ) => ( {
			...prev,
			casesPerMonth: {
				...prev.casesPerMonth,
				[ id ]: indication.defaultCasesPerMonth,
			},
			labOutsourcingCost: {
				...prev.labOutsourcingCost,
				[ id ]: indication.defaultLabOutsourcingCost,
			},
			materialCost: {
				...prev.materialCost,
				[ id ]: indication.defaultMaterialCost,
			},
			designCost: {
				...prev.designCost,
				[ id ]: indication.defaultDesignCost,
			},
			designSource: {
				...prev.designSource,
				[ id ]: defaultDesignSource( indication ),
			},
		} ) );
	};

	// Reference tool's AI / SR / My Lab pills: picking one sets the design
	// cost to that source's preset (AI is always free; My Lab clears the
	// field for the user's own figure, matching the reference's behavior
	// when switching to "Mein Labor"). Typing directly into the field is
	// implicitly "My Lab" — see changeDesignCost below.
	const selectDesignSource = ( id, source ) => {
		const indication = allIndications.find( ( row ) => row.id === id );

		if ( ! indication ) {
			return;
		}

		const cost = source === 'sr' ? indication.srDesignCost : 0;

		setIndicationState( ( prev ) => ( {
			...prev,
			designCost: { ...prev.designCost, [ id ]: cost },
			designSource: { ...prev.designSource, [ id ]: source },
		} ) );
	};

	const changeDesignCost = ( id, value ) =>
		setIndicationState( ( prev ) => ( {
			...prev,
			designCost: { ...prev.designCost, [ id ]: value },
			designSource: { ...prev.designSource, [ id ]: 'custom' },
		} ) );

	// Re-selecting the current product already resets every field it owns
	// (price paid, material, all indication rows, lease terms) back to that
	// product's defaults — so "reset everything" is just that, rather than
	// a second, separately-maintained reset path. The only thing it doesn't
	// cover is per-row reset for indications belonging to a *different*
	// product than the currently selected one (not applicable — only the
	// selected product's rows are ever shown).
	const resetAll = () => {
		if ( selectedProduct ) {
			selectProduct( selectedProduct );
		}
	};

	return (
		<div className="roi-calculator">
			<div className="roi-calculator__top-utility">
				<LanguageSwitcher
					languageLinks={ settings.languageLinks }
					{ ...languageSwitcher }
				/>
				<span className="roi-calculator__header-contact-text">
					sprintray.com
				</span>
			</div>
			<div className="roi-calculator__top">
				<div className="roi-calculator__top-main">
					<Header calculatorType={ calculatorType } />
					{ settings.introText && (
						<div
							className="roi-calculator__intro"
							// Trusted admin-authored ACF WYSIWYG content.
							dangerouslySetInnerHTML={ {
								__html: settings.introText,
							} }
						/>
					) }
				</div>
				<div className="roi-calculator__top-side">
					<InfoSidebar
						partnerLabNote={ settings.partnerLabNote }
						calculationMethod={ settings.calculationMethod }
					/>
				</div>
			</div>
			<div className="roi-calculator__top-divider" />

			<div className="roi-calculator__toolbar">
				<button
					type="button"
					className="roi-calculator__reset-all"
					onClick={ resetAll }
				>
					↺ { __( 'Reset all values', 'sprintray-roi-calculator' ) }
				</button>
			</div>

			<ProductStep
				products={ products }
				selectedProductId={ selectedProductId }
				pricePaid={ pricePaid }
				onSelectProduct={ selectProduct }
				onChangePrice={ setPricePaid }
				currency={ currency }
				locale={ locale }
			/>

			{ availableCapsuleSizes.length > 1 && (
				<CapsuleSizeStep
					capsuleSizes={ availableCapsuleSizes.map( ( value ) => ( {
						value,
						label: settings.capsuleSizeLabels?.[ value ] ?? value,
					} ) ) }
					selectedCapsuleSize={ selectedCapsuleSize }
					onSelectCapsuleSize={ selectCapsuleSize }
				/>
			) }

			{ selectedProduct?.hasMaterialStep && (
				<MaterialStep
					materials={ capsuleFilteredMaterials }
					selectedMaterialId={ selectedMaterialId }
					onSelectMaterial={ selectMaterial }
					currency={ currency }
					locale={ locale }
				/>
			) }

			{ isLabor && (
				<LeasingStep
					leaseTermMonths={ leaseTermMonths }
					annualInterestRate={ annualInterestRate }
					calculatedMonthlyRate={ calculatedMonthlyRate }
					contractualRateOverride={ contractualRateOverride }
					onChangeTerm={ setLeaseTermMonths }
					onChangeInterestRate={ setAnnualInterestRate }
					onChangeContractualRate={ setContractualRateOverride }
					currency={ currency }
					locale={ locale }
				/>
			) }

			<IndicationsTable
				indications={ capsuleFilteredIndications }
				activeMaterialName={
					selectedProduct?.hasMaterialStep ? activeMaterialName : null
				}
				enabledMap={ indicationState.enabledIndications }
				showDescriptions={ !! settings.showIndicationDescriptions }
				casesPerMonth={ indicationState.casesPerMonth }
				priceByIndication={ indicationState.labOutsourcingCost }
				materialCost={ indicationState.materialCost }
				designCost={ isLabor ? null : indicationState.designCost }
				designSource={ isLabor ? null : indicationState.designSource }
				priceColumnLabel={
					isLabor
						? __(
								'Billing Price / Case',
								'sprintray-roi-calculator'
						  )
						: __(
								'Lab Outsourcing Cost',
								'sprintray-roi-calculator'
						  )
				}
				outcomeColumnLabel={
					isLabor
						? __(
								'Gross Profit / Month',
								'sprintray-roi-calculator'
						  )
						: __( 'Savings / Month', 'sprintray-roi-calculator' )
				}
				computeOutcome={ ( { price, material, design, cases } ) =>
					( price - material - design ) * cases
				}
				onToggle={ toggleIndication }
				onChangeCases={ updateIndicationField( 'casesPerMonth' ) }
				onChangePrice={ updateIndicationField( 'labOutsourcingCost' ) }
				onChangeMaterialCost={ updateIndicationField( 'materialCost' ) }
				onChangeDesignCost={ isLabor ? undefined : changeDesignCost }
				onSelectDesignSource={
					isLabor ? undefined : selectDesignSource
				}
				onReset={ resetIndication }
				currency={ currency }
			/>

			{ ! leadSubmitted && (
				<LeadFormGate
					leadForm={ leadForm }
					pdfUrl={ storedPdfUrl( pdfSettings, storedPdfId ) }
					onSubmitted={ handleLeadSubmitted }
				/>
			) }

			{ leadSubmitted && ! isLabor && (
				<ResultsStep
					annualSavings={ results.annualSavings }
					netProfitYearOne={ results.netProfitYearOne }
					monthsForRoi={ results.monthsForRoi }
					monthlySavings={ results.monthlySavings }
					devicePricePaid={ pricePaid }
					productName={ selectedProduct?.name }
					annualLabCost={ results.annualLabCost }
					annualInternalCost={ results.annualInternalCost }
					cumulativeSavings={ results.cumulativeSavings }
					currency={ currency }
					locale={ locale }
				/>
			) }

			{ leadSubmitted && isLabor && (
				<LaborResultsStep
					monthlyGrossProfit={ results.monthlyGrossProfit }
					effectiveMonthlyRate={ results.effectiveMonthlyRate }
					netCashflowMonthly={ results.netCashflowMonthly }
					annualNetCashflow={ results.annualNetCashflow }
					cumulativeCashflow={ results.cumulativeCashflow }
					currency={ currency }
					locale={ locale }
				/>
			) }

			{ leadSubmitted && pdfSettings && (
				<PdfDownloadButton
					pdfSettings={ pdfSettings }
					getInput={ buildPdfInput }
				/>
			) }

			{ leadSubmitted && ! isLabor && hasVisibleOptionalLayers && (
				<OptionalLayersStep
					layers={ optionalLayers }
					hasMaterialStep={ !! selectedProduct?.hasMaterialStep }
					freedSlotsPerMonth={ freedSlotsPerMonth }
					chairTimeLayerEnabled={ chairTimeLayerEnabled }
					onToggleChairTimeLayer={ () =>
						setChairTimeLayerEnabled( ( prev ) => ! prev )
					}
					replacementActFee={ replacementActFee }
					onChangeReplacementActFee={ setReplacementActFee }
					targetConversionRate={ targetConversionRate }
					onChangeTargetConversionRate={ setTargetConversionRate }
					provisionalCasesAvoided={ provisionalCasesAvoided }
					onChangeProvisionalCasesAvoided={
						setProvisionalCasesAvoided
					}
					savingPerProvisional={ savingPerProvisional }
					onChangeSavingPerProvisional={ setSavingPerProvisional }
					chairTimeMonthly={ chairTimeLayer.chairTimeMonthly }
					chairTimeAnnualPotential={ chairTimeLayer.annualPotential }
					financingLayerEnabled={ financingLayerEnabled }
					onToggleFinancingLayer={ () =>
						setFinancingLayerEnabled( ( prev ) => ! prev )
					}
					leaseTermMonths={ leaseTermMonths }
					onChangeLeaseTermMonths={ setLeaseTermMonths }
					annualInterestRate={ annualInterestRate }
					onChangeAnnualInterestRate={ setAnnualInterestRate }
					calculatedMonthlyRate={ calculatedMonthlyRate }
					contractualRateOverride={ contractualRateOverride }
					onChangeContractualRateOverride={
						setContractualRateOverride
					}
					effectiveMonthlyRate={ financingLayer.effectiveMonthlyRate }
					netMonthlyCashFlow={ financingLayer.netMonthlyCashFlow }
					cameraLayerEnabled={ cameraLayerEnabled }
					onToggleCameraLayer={ () =>
						setCameraLayerEnabled( ( prev ) => ! prev )
					}
					cameraMonthlyPayment={ cameraMonthlyPayment }
					onChangeCameraMonthlyPayment={ setCameraMonthlyPayment }
					adjustedRoiMonths={ cameraLayer.adjustedRoiMonths }
					addedMonths={ cameraLayer.addedMonths }
					monthlySavings={ results.monthlySavings }
					monthsForRoi={ results.monthsForRoi }
					currency={ currency }
					locale={ locale }
				/>
			) }

			{ settings.noticeText && (
				<div
					className="roi-calculator__notice"
					// Trusted admin-authored ACF WYSIWYG content.
					dangerouslySetInnerHTML={ { __html: settings.noticeText } }
				/>
			) }
		</div>
	);
};

Calculator.propTypes = {
	settings: PropTypes.object,
	languageSwitcher: PropTypes.object,
};

// Holds the language picked in the on-page switcher (Languages.php) and
// hands Calculator the settings in that language. Calculator itself stays
// mounted across a switch, so every value entered is kept.
const RoiCalculator = ( { settings } ) => {
	const languages = settings?.languages ?? [];
	const [ languageCode, setLanguageCode ] = useState(
		() => languages.find( ( language ) => language.isSite )?.code ?? null
	);
	const [ switching, setSwitching ] = useState( false );
	const language =
		languages.find( ( candidate ) => candidate.code === languageCode ) ??
		null;
	const localizedSettings = useMemo(
		() => localizeSettings( settings, language ),
		[ settings, language ]
	);

	const selectLanguage = ( next ) => {
		setSwitching( true );
		loadLanguage( next )
			.then( () => setLanguageCode( next.code ) )
			.catch( ( error ) =>
				// eslint-disable-next-line no-console
				console.error( 'ROI Calculator:', error )
			)
			.finally( () => setSwitching( false ) );
	};

	return (
		<Calculator
			settings={ localizedSettings }
			languageSwitcher={
				languages.length > 1
					? {
							languages,
							currentLanguage: languageCode,
							onSelectLanguage: selectLanguage,
							disabled: switching,
					  }
					: null
			}
		/>
	);
};

RoiCalculator.propTypes = {
	settings: PropTypes.object,
};

export default RoiCalculator;
